/**
 * Generic Catalog Form Page
 *
 * Mobile-first form for creating/editing catalog items.
 */

import { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Save, Loader2 } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import api from '@/lib/api'
import { useTranslation } from '@/hooks/useTranslation'
import { useToast } from '@/components/ui/toaster'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'

interface FormField {
  key: string
  label: string
  type: 'text' | 'email' | 'textarea' | 'tel' | 'select'
  required?: boolean
  placeholder?: string
  options?: { value: string; label: string }[]
}

interface CatalogFormConfig {
  endpoint: string
  basePath: string
  title: string
  newTitle: string
  editTitle: string
  fields: FormField[]
  icon: React.ComponentType<{ className?: string }>
}

interface CatalogFormPageProps {
  config: CatalogFormConfig
}

export function CatalogFormPage({ config }: CatalogFormPageProps) {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const isEditing = !!id && id !== 'new'
  const Icon = config.icon

  // Build dynamic schema based on fields
  const schemaFields: Record<string, z.ZodTypeAny> = {}
  config.fields.forEach((field) => {
    if (field.required) {
      if (field.type === 'email') {
        schemaFields[field.key] = z.string().min(1, t('common.required')).email(t('common.invalidEmail'))
      } else {
        schemaFields[field.key] = z.string().min(1, t('common.required'))
      }
    } else {
      if (field.type === 'email') {
        schemaFields[field.key] = z.string().email(t('common.invalidEmail')).optional().or(z.literal(''))
      } else {
        schemaFields[field.key] = z.string().optional()
      }
    }
  })
  const formSchema = z.object(schemaFields)
  type FormData = z.infer<typeof formSchema>

  // Fetch existing item if editing
  const { data: existingItem, isLoading: isLoadingItem } = useQuery({
    queryKey: [config.endpoint, id],
    queryFn: async () => {
      const response = await api.get(`/catalogs/${config.endpoint}/${id}`)
      return response.data
    },
    enabled: isEditing,
  })

  // Form setup
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: config.fields.reduce((acc, field) => {
      acc[field.key] = ''
      return acc
    }, {} as Record<string, string>),
  })

  // Reset form when existing item loads
  useEffect(() => {
    if (existingItem && isEditing) {
      const values: Record<string, string> = {}
      config.fields.forEach((field) => {
        values[field.key] = existingItem[field.key] || ''
      })
      reset(values, { keepDirty: false, keepDefaultValues: false })
    }
  }, [existingItem, isEditing, config.fields, reset])

  const onApiError = (err: unknown) => {
    const e = err as { response?: { status?: number; data?: { detail?: string } } }
    toast({
      title: t('common.error'),
      description:
        e?.response?.status === 409
          ? t('catalogs.identificationInUse')
          : e?.response?.data?.detail || t('common.saveFailed'),
      variant: 'destructive',
    })
  }

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: FormData) => {
      const response = await api.post(`/catalogs/${config.endpoint}`, data)
      return response.data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [config.endpoint] })
      navigate(`${config.basePath}/${data.id}`)
    },
    onError: onApiError,
  })

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: FormData) => {
      const response = await api.put(`/catalogs/${config.endpoint}/${id}`, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [config.endpoint] })
      queryClient.invalidateQueries({ queryKey: [config.endpoint, id] })
      navigate(`${config.basePath}/${id}`)
    },
    onError: onApiError,
  })

  const onSubmit = (data: FormData) => {
    if (isEditing) {
      updateMutation.mutate(data)
    } else {
      createMutation.mutate(data)
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending

  if (isEditing && isLoadingItem) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-8 w-48" />
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="h-10 w-10 shrink-0"
          onClick={() => navigate(isEditing ? `${config.basePath}/${id}` : config.basePath)}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl md:text-2xl font-bold">
            {isEditing ? config.editTitle : config.newTitle}
          </h1>
          <p className="text-sm text-muted-foreground">{config.title}</p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Icon className="h-4 w-4" />
              {t('common.details')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {config.fields.map((field) => (
              <div key={field.key} className="space-y-2">
                <Label htmlFor={field.key}>
                  {field.label}
                  {field.required && <span className="text-red-500 ml-1">*</span>}
                </Label>
                {field.type === 'textarea' ? (
                  <textarea
                    id={field.key}
                    className="w-full min-h-[100px] px-3 py-2 rounded-md border border-input bg-background text-base resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder={field.placeholder}
                    {...register(field.key)}
                  />
                ) : field.type === 'select' ? (
                  <select
                    id={field.key}
                    className="w-full h-12 px-3 rounded-md border border-input bg-background text-base focus:outline-none focus:ring-2 focus:ring-ring"
                    {...register(field.key)}
                  >
                    <option value="">{t('common.select')}</option>
                    {field.options?.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <Input
                    id={field.key}
                    type={field.type === 'tel' ? 'tel' : field.type}
                    className="h-12"
                    placeholder={field.placeholder}
                    {...register(field.key)}
                  />
                )}
                {errors[field.key] && (
                  <p className="text-sm text-red-500">
                    {errors[field.key]?.message as string}
                  </p>
                )}
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Submit Button */}
        <Button
          type="submit"
          className="w-full h-12 mt-4"
          disabled={isPending}
        >
          {isPending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          {t('common.save')}
        </Button>
      </form>
    </div>
  )
}

export type { CatalogFormConfig, FormField }
